import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Truck, RefreshCw, Ban, AlertTriangle, Route } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtMoney } from "@/components/erp";
import { useAuth } from "@/context/AuthContext";
import { can, DURUM, UYARI, SEVK_DURUM, Rozet } from "@/lib/perms";
import api, { errMsg } from "@/lib/api";
import { SevkiyatDialog } from "@/components/SevkiyatDialog";
import { ToplamaRotasi } from "@/components/depo/ToplamaRotasi";
import { IrsaliyeDialog, Tasima } from "@/components/Irsaliye";

export const DurumBadge = ({ durum, testid }) => {
  const [l, c] = DURUM[durum] || [durum, "bg-white/5"];
  return <span data-testid={testid} className={`text-[11px] px-2.5 py-0.5 rounded-full border whitespace-nowrap ${c}`}>{l}</span>;
};

const Aksiyon = ({ onClick, icon: Icon, children, testid, tone = "" }) => (
  <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border border-white/10 hover:bg-white/10 transition-colors ${tone}`}>
    <Icon className="w-4 h-4" />{children}
  </button>
);

const SevkListe = ({ list, onAc }) => (
  <div className="mt-6" data-testid="detay-sevkiyatlar">
    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Sevkiyatlar</div>
    <div className="space-y-2">{list.map((v) => (
      <button key={v.id} onClick={() => onAc(v.id)} data-testid={`detay-sevkiyat-${v.no}`} className="w-full text-left rounded-xl bg-white/[0.04] hover:bg-white/[0.07] p-3 transition-colors">
        <div className="flex justify-between gap-2 text-sm"><span className="font-mono text-xs">{v.no} · {v.tarih}</span><Rozet map={SEVK_DURUM} d={v.durum} /></div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground"><Tasima v={v} />{v.kalemler.map((k) => <span key={k.kalem_idx}>{k.kod} × {k.miktar}</span>)}</div>
      </button>
    ))}</div>
  </div>
);

export const SiparisDetay = ({ s, onClose, onChanged }) => {
  const { user } = useAuth();
  const [not_, setNot] = useState("");
  const [sevkler, setSevkler] = useState([]);
  const [sevkAc, setSevkAc] = useState(false);
  const [rota, setRota] = useState(false);
  const [irsaliye, setIrsaliye] = useState(null);
  useEffect(() => {
    if (can(user, "sevkiyat:read")) api.get(`/sevkiyatlar?siparis_id=${s.id}`).then((r) => setSevkler(r.data));
  }, [s.id, s.durum, s.sevk_sayisi, user]);
  const sevkEdilebilir = ["uretimde", "sevke_hazir"].includes(s.durum) && s.kalemler.some((k) => k.rezerve_miktar > 1e-9) && can(user, "siparis:sevk");
  const islem = async (yol, mesaj) => {
    try {
      const { data } = await api.post(`/siparisler/${s.id}/${yol}`, yol === "stok-yenile" ? undefined : { not: not_ });
      toast.success(mesaj);
      onChanged(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const p = s.para_birimi;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="glass w-full sm:max-w-2xl overflow-y-auto erp-scroll" data-testid="siparis-detay">
        <SheetHeader>
          <SheetTitle className="font-head text-2xl flex items-center gap-3"><span data-testid="detay-no">{s.no}</span><DurumBadge durum={s.durum} testid="detay-durum" /></SheetTitle>
          <div className="text-sm text-muted-foreground">{s.cari_unvan} · {s.tarih} · Termin <span className="text-white">{s.termin_tarihi}</span>{s.onerilen_termin ? ` · En erken üretim: ${s.onerilen_termin}` : ""}</div>
        </SheetHeader>
        {s.uyarilar?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="detay-uyarilar">
            {s.uyarilar.map((u) => <span key={u} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30"><AlertTriangle className="w-3 h-3" />{UYARI[u]}</span>)}
          </div>
        )}
        {s.durum === "onay_bekliyor" && (
          <div className="mt-3 text-xs text-muted-foreground" data-testid="detay-onaylayici">Onay sırası: {s.onaylayici?.tip === "temsilci" ? "Satış temsilcisi" : "Yönetici"}</div>
        )}
        <div className="mt-5 space-y-2">
          {s.kalemler.map((k, i) => (
            <div key={i} className="rounded-xl bg-white/[0.04] p-4" data-testid={`detay-kalem-${k.kod}`}>
              <div className="flex justify-between gap-2 text-sm">
                <span><span className="font-mono text-xs text-muted-foreground mr-2">{k.kod}</span>{k.ad}</span>
                <span className="font-mono">{fmtMoney(k.tutar, p)}</span>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground mt-2">
                <span>{k.miktar} {k.birim} × {fmtMoney(k.birim_fiyat, p)}</span>
                <span className={k.min_alti ? "text-amber-400" : ""}>Min {fmtMoney(k.min_fiyat, p)}</span>
                <span className={k.rezerve_miktar > 0 ? "text-emerald-400" : ""} data-testid={`detay-rezerve-${k.kod}`}>Ayrılan {k.rezerve_miktar}</span>
                {k.eksik_miktar > 0 && <span className="text-violet-300" data-testid={`detay-eksik-${k.kod}`}>Üretime {k.eksik_miktar}</span>}
                {k.sevk_miktar > 0 && <span className="text-sky-300" data-testid={`detay-sevk-${k.kod}`}>Sevk {k.sevk_miktar}</span>}
                {k.sevk_miktar > 0 && k.miktar - k.sevk_miktar > 1e-9 && <span className="text-amber-300" data-testid={`detay-kalan-${k.kod}`}>Kalan {+(k.miktar - k.sevk_miktar).toFixed(4)}</span>}
              </div>
              {k.rezervasyonlar?.length > 0 && <div className="text-[11px] text-muted-foreground mt-1">Rezervasyon: {k.rezervasyonlar.map((r) => `${r.miktar}${r.lot ? ` (lot ${r.lot})` : ""}`).join(" · ")}</div>}
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-white/5 p-4 mt-4 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-muted-foreground">Ara toplam</span><span className="font-mono">{fmtMoney(s.ara_toplam, p)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">KDV</span><span className="font-mono">{fmtMoney(s.kdv_toplam, p)}</span></div>
          <div className="flex justify-between font-semibold"><span>Genel toplam</span><span className="font-mono">{fmtMoney(s.genel_toplam, p)}</span></div>
          {p !== "TRY" && <div className="flex justify-between text-xs text-muted-foreground"><span>TL (kur {s.kur})</span><span className="font-mono">{fmtMoney(s.genel_toplam_try)}</span></div>}
          <div className="flex justify-between text-xs text-muted-foreground pt-1"><span>Risk (sipariş anı)</span><span className="font-mono">{fmtMoney(s.risk?.yeni_kullanim)} / {s.risk?.limit ? fmtMoney(s.risk.limit) : "tanımsız"}</span></div>
        </div>
        <div className="mt-5 space-y-3">
          {s.onaylayabilir && <Input data-testid="detay-not" placeholder="Onay / red notu (opsiyonel)" className="bg-white/5" value={not_} onChange={(e) => setNot(e.target.value)} />}
          <div className="flex flex-wrap gap-2">
            {s.onaylayabilir && <Aksiyon testid="detay-onayla" icon={Check} tone="text-emerald-300" onClick={() => islem("onayla", "Onaylandı ve işleme alındı")}>Onayla</Aksiyon>}
            {s.onaylayabilir && <Aksiyon testid="detay-reddet" icon={X} tone="text-red-300" onClick={() => islem("reddet", "Reddedildi")}>Reddet</Aksiyon>}
            {s.durum === "uretimde" && can(user, "siparis:sevk") && <Aksiyon testid="detay-stok-yenile" icon={RefreshCw} onClick={() => islem("stok-yenile", "Stok yeniden kontrol edildi")}>Stoğu yeniden kontrol et</Aksiyon>}
            {sevkEdilebilir && <Aksiyon testid="detay-sevk" icon={Truck} tone="text-sky-300" onClick={() => setSevkAc(true)}>{s.durum === "uretimde" ? "Hazır olanı sevk et" : "Sevk et"}</Aksiyon>}
            {can(user, "sevkiyat:read") && s.kalemler.some((k) => k.rezerve_miktar > 0) && <Aksiyon testid="detay-toplama-rotasi" icon={Route} tone="text-emerald-300" onClick={() => setRota(true)}>Toplama rotası</Aksiyon>}
            {!["sevk_edildi", "iptal", "reddedildi"].includes(s.durum) && can(user, "siparis:cancel") && <Aksiyon testid="detay-iptal" icon={Ban} tone="text-red-300" onClick={() => islem("iptal", "İptal edildi, rezervasyon bırakıldı")}>İptal</Aksiyon>}
          </div>
        </div>
        {sevkler.length > 0 && <SevkListe list={sevkler} onAc={(id) => setIrsaliye({ id })} />}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Geçmiş</div>
          <ol className="space-y-2 border-l border-white/10 pl-4" data-testid="detay-gecmis">
            {(s.gecmis || []).map((g, i) => (
              <li key={i} className="text-xs"><span className="text-white">{g.olay.replace(/_/g, " ")}</span> · {g.kullanici} · <span className="text-muted-foreground">{new Date(g.tarih).toLocaleString("tr-TR")}</span>{g.aciklama ? <div className="text-muted-foreground">{g.aciklama}</div> : null}</li>
            ))}
          </ol>
        </div>
        {rota && <ToplamaRotasi siparisId={s.id} onClose={() => setRota(false)} onSevk={async (v) => { setRota(false); setIrsaliye({ id: v.id }); onChanged((await api.get(`/siparisler/${s.id}`)).data); }} />}
        {sevkAc && <SevkiyatDialog s={s} onClose={() => setSevkAc(false)} onSaved={async (v, yazdir) => { setSevkAc(false); setIrsaliye({ id: v.id, yazdir }); onChanged((await api.get(`/siparisler/${s.id}`)).data); }} />}
        {irsaliye && <IrsaliyeDialog id={irsaliye.id} yazdir={irsaliye.yazdir} onClose={() => setIrsaliye(null)} />}
      </SheetContent>
    </Sheet>
  );
};
